import { useShallow } from 'zustand/shallow'

import { Button } from '@/components/atoms/Button'
import { Dialog, DialogContent, DialogTitle } from '@/components/atoms/Dialog'
import useExpandedPortsDialog from '@/hooks/useExpandedPortsDialog'
import useStepperControls from '@/hooks/useStepperControls'
import { useScopedI18n } from '@/locales/client'
import type { Item } from '@/stores/CartStore'
import type { DialogTypes, WarningDialog as WarningDialogType } from '@/stores/DialogsStore'
import useDialogsStore from '@/stores/DialogsStore'
import type { RequiredNotNull } from '@/util/type-utilities'

export type WarningDialogTypes = 'temperatureSensor' | 'powerCenter'

type ContentProps = Omit<RequiredNotNull<WarningDialogType>, 'open'>
type TemperatureSensorProps = Pick<ContentProps, 'incomingItem' | 'itemWithSensorIncluded'>

const DIALOG_ID: DialogTypes = 'warning'
// const B = ({ children }: PropsWithChildren) => <span className="font-bold">{children}</span>

const TemperatureSensor = ({ incomingItem, itemWithSensorIncluded }: TemperatureSensorProps) => {
  const t = useScopedI18n('dialogs.warning')
  return (
    <p className="text-pretty text-2xl font-semibold leading-snug">
      {t('temperatureSensor', {
        incomingItem: incomingItem.name,
        itemWithSensorIncluded: itemWithSensorIncluded.name,
      })}
    </p>
  )
}

const PowerCenter = () => {
  const t = useScopedI18n('dialogs.warning')
  return (
    <p className="text-pretty text-2xl font-semibold leading-snug">
      {t('powerCenter')}
    </p>
  )
}

// ? Separated component so this is only used when item is not null
function Content({ incomingItem, warningType, itemWithSensorIncluded }: Omit<ContentProps, 'itemWithSensorIncluded'> & { itemWithSensorIncluded: Item | null }) {
  const t = useScopedI18n('dialogs.warning')
  const toggle = useDialogsStore(useShallow(state => state.toggle))
  const { increase } = useStepperControls(incomingItem)
  const expandedPorts = useExpandedPortsDialog(incomingItem)

  const handleCancel = () => {
    toggle(DIALOG_ID)
  }
  const handleConfirm = () => {
    increase()
    toggle(DIALOG_ID)
    expandedPorts.check()
  }

  return (
    <DialogContent className="flex flex-col items-center justify-center gap-8 p-12 text-center">
      <DialogTitle>
        {warningType === 'temperatureSensor' && itemWithSensorIncluded
          ? (
              <TemperatureSensor
                incomingItem={incomingItem}
                itemWithSensorIncluded={itemWithSensorIncluded}
              />
            )
          : <PowerCenter />}
      </DialogTitle>

      <section className="flex flex-col gap-3 *:font-semibold">
        <Button onClick={handleCancel}>
          {t('cancel')}
        </Button>
        <Button variant="outline" onClick={handleConfirm}>
          {t('confirm')}
        </Button>
      </section>
    </DialogContent>
  )
}

function WarningDialog() {
  const { open, warningType, incomingItem, itemWithSensorIncluded } = useDialogsStore(useShallow(state => state.dialogs.warning))
  const toggle = useDialogsStore(useShallow(state => state.toggle))

  return (
    <Dialog open={open} onOpenChange={() => toggle(DIALOG_ID)}>
      {incomingItem && warningType && (
        <Content
          incomingItem={incomingItem}
          itemWithSensorIncluded={itemWithSensorIncluded}
          warningType={warningType}
        />
      )}
    </Dialog>
  )
}

export default WarningDialog
